/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState, useEffect } from "react";
import {
  Modal,
  Paper,
  Box,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  CircularProgress,
} from "@mui/material";
import { 
  StyledTitle,
  StyledDescription,
  StyledConfirmation,
  OptionLabel,
  OptionDescription,
  StyledEnforceTitle,
} from './styles'
import { ModalCancelButton } from "../modal_cancel_button/index";

const MfaConfigurationModal = ({ 
  open, 
  onClose, 
  onSave, 
  currentStatus, 
  currentEnforcement, 
  loading 
}) => {
  const [mfaEnabled, setMfaEnabled] = useState(currentStatus || false);
  const [enforcement, setEnforcement] = useState(currentEnforcement || 'mandatory');

  useEffect(() => {
    if (open) {
      setMfaEnabled(currentStatus);
      setEnforcement(currentEnforcement || 'mandatory');
    }
  }, [open, currentStatus, currentEnforcement]);


  const handleSave = () => {
    if (mfaEnabled) {
      onSave(true, enforcement);
    } else {
      onSave(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper
        sx={{
          width: 500,
          p: 3,
          borderRadius: 2,
          bgcolor: 'background.paper',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.12)',
        }}
      >
        <StyledTitle>
          MFA Configuration
        </StyledTitle>
        
        <StyledDescription variant="body2" color="text.secondary">
          Add an extra layer of security to user accounts by asking users to verify their identity when they enter a username and password.
        </StyledDescription>

        {/* Enable/Disable Checkbox */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={mfaEnabled}
                onChange={(e) => setMfaEnabled(e.target.checked)}
                color="primary"
                sx={{ 
                  '&.Mui-checked': {
                    color: '#395bbd',
                  },
                }}
              />
            }
            label={
              <StyledConfirmation variant="body2">
                Allow users to turn on MFA Verification
              </StyledConfirmation>
            }
            sx={{ 
              alignItems: 'flex-start',
              '& .MuiFormControlLabel-label': {
                marginTop: '2px',
              }
            }}
          />
        </Box>

        {/* Enforcement Section - Only shown when MFA is enabled */}
        {mfaEnabled && (
          <Box sx={{ 
            mb: 3, 
            pl: 3, 
            borderLeft: '2px solid', 
            borderColor: 'divider',
            backgroundColor: '#fafafa',
            p: 2,
            borderRadius: 1,
          }}>
            <StyledEnforceTitle variant="subtitle2">
              Enforcement
            </StyledEnforceTitle>
            
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <RadioGroup
                value={enforcement}
                onChange={(e) => setEnforcement(e.target.value)}
              >
                <FormControlLabel
                  value="mandatory"
                  control={
                    <Radio 
                      sx={{ 
                        '&.Mui-checked': {
                          color: '#395bbd',
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <OptionLabel variant="body2">
                        Mandatory
                      </OptionLabel>
                      <OptionDescription variant="caption" color="text.secondary">
                        Users must enroll in MFA immediately and cannot skip
                      </OptionDescription>
                    </Box>
                  }
                  sx={{ mb: 1.5 }}
                />
                <FormControlLabel
                  value="optional"
                  control={
                    <Radio 
                      sx={{ 
                        '&.Mui-checked': {
                          color: '#395bbd',
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <OptionLabel variant="body2">
                        Optional
                      </OptionLabel>
                      <OptionDescription variant="caption" color="text.secondary">
                        Users can skip enrollment and set up MFA later
                      </OptionDescription>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 1, 
          mt: 3, 
          pt: 2, 
          borderTop: 1, 
          borderColor: 'divider' 
        }}>
          <ModalCancelButton
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </ModalCancelButton>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#395bbd',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#001b59',
              },
              '&.Mui-disabled': {
                backgroundColor: '#395bbd',
                opacity: 0.6,
              },
            }}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default MfaConfigurationModal;
